import React, { useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Row, Col, Card, CardBody, Nav, NavItem, NavLink, Table } from 'reactstrap';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import AuthContext from '../../context/AuthContext';
import handleUnauthorized401QueryError from '../../utils/handleUnauthorized401QueryError';
import { getDashboardStats } from '../../api/api';
import Loader from '../../components/Loader';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const PERIODS = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This Week' },
    { key: 'month', label: 'This Month' },
    { key: 'year', label: 'This Year' },
];

function formatCurrency(value) {
    const num = parseFloat(value) || 0;
    if (num >= 1_000_000) return `₹${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `₹${(num / 1_000).toFixed(1)}K`;
    return `₹${num.toFixed(2)}`;
}

function calcChange(current, previous) {
    const cur = parseFloat(current) || 0;
    const prev = parseFloat(previous) || 0;
    if (prev === 0) return null;
    return ((cur - prev) / prev) * 100;
}

const SINCE_LABEL = {
    today: 'Since yesterday',
    week: 'Since last week',
    month: 'Since last month',
    year: 'Since last year',
};

function StatCard({ title, value, iconClass, pct, period, loading }) {
    const up = pct === null ? null : pct >= 0;
    return (
        <div className="card-box">
            <div className="d-flex justify-content-between align-items-start mb-2">
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>{title}</p>
                <div style={{
                    background: 'rgba(59,175,218,0.15)',
                    borderRadius: 6,
                    width: 38,
                    height: 38,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <i className={`${iconClass} text-primary`} style={{ fontSize: 20 }} />
                </div>
            </div>
            {loading
                ? <div className="my-2"><Loader size="small" /></div>
                : <h3 className="mb-3 mt-1" style={{ fontWeight: 700 }}>{value}</h3>}
            <div className="d-flex align-items-center" style={{ fontSize: 13 }}>
                {pct !== null && (
                    <span className={up ? 'text-success' : 'text-danger'} style={{ marginRight: 6 }}>
                        <i className={`bx ${up ? 'bxs-up-arrow' : 'bxs-down-arrow'}`} style={{ fontSize: 9, marginRight: 3 }} />
                        {Math.abs(pct).toFixed(2)}%
                    </span>
                )}
                <span className="text-muted">{SINCE_LABEL[period]}</span>
            </div>
        </div>
    );
}

function Dashboard() {
    const { refreshToken } = useContext(AuthContext);
    const [period, setPeriod] = useState('today');

    const { data, isFetching, error, refetch } = useQuery({
        queryKey: ['dashboardStats', period],
        queryFn: () => getDashboardStats(period),
        retry: false,
        onError: (err) => {
            handleUnauthorized401QueryError(err, refreshToken, refetch);
        },
    });

    const loading = isFetching;
    const stats = data || {};
    const topProducts = stats.top_products || [];
    const locations = stats.revenue_by_location || [];

    // Build a set of country names from location data for map coloring
    const countryRevenueMap = {};
    locations.forEach(loc => {
        if (loc.country) {
            const key = loc.country.trim().toLowerCase();
            countryRevenueMap[key] = (countryRevenueMap[key] || 0) + parseFloat(loc.revenue || 0);
        }
    });
    const maxRevenue = Math.max(...Object.values(countryRevenueMap), 1);

    return (
        <div className="container-fluid">
            {/* Page title */}
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="mb-0">Dashboard</h4>
                {/* Period tabs */}
                <Nav pills>
                    {PERIODS.map(p => (
                        <NavItem key={p.key}>
                            <NavLink
                                active={period === p.key}
                                onClick={() => setPeriod(p.key)}
                                style={{ cursor: 'pointer', fontSize: 13 }}
                            >
                                {p.label}
                            </NavLink>
                        </NavItem>
                    ))}
                </Nav>
            </div>

            {error && !loading && (
                <div className="alert alert-danger">
                    Failed to load dashboard data. Please try again.
                </div>
            )}

            {/* Stat cards row */}
            <Row>
                <Col md={6} xl={3}>
                    <StatCard
                        title="Total Orders"
                        value={stats.total_orders ?? 0}
                        iconClass="bx bx-shopping-bag"
                        pct={calcChange(stats.total_orders, stats.prev_total_orders)}
                        period={period}
                        loading={loading}
                    />
                </Col>
                <Col md={6} xl={3}>
                    <StatCard
                        title="Total Revenue"
                        value={formatCurrency(stats.total_revenue)}
                        iconClass="bx bx-rupee"
                        pct={calcChange(stats.total_revenue, stats.prev_total_revenue)}
                        period={period}
                        loading={loading}
                    />
                </Col>
                <Col md={6} xl={3}>
                    <StatCard
                        title="New Customers"
                        value={stats.new_customers ?? 0}
                        iconClass="bx bx-user-plus"
                        pct={calcChange(stats.new_customers, stats.prev_new_customers)}
                        period={period}
                        loading={loading}
                    />
                </Col>
                <Col md={6} xl={3}>
                    <StatCard
                        title="Online Orders"
                        value={stats.online_orders ?? 0}
                        iconClass="bx bx-credit-card"
                        pct={calcChange(stats.online_orders, stats.prev_online_orders)}
                        period={period}
                        loading={loading}
                    />
                </Col>
            </Row>

            {/* Map + Top Products row */}
            <Row>
                {/* Revenue by Location */}
                <Col xl={6}>
                    <Card className="card-box mb-4">
                        <CardBody>
                            <h5 className="header-title mb-3">Revenue by Location</h5>
                            <ComposableMap
                                projectionConfig={{ scale: 147 }}
                                style={{ width: '100%', height: 'auto' }}
                            >
                                <Geographies geography={GEO_URL}>
                                    {({ geographies }) =>
                                        geographies.map(geo => {
                                            const geoName = (geo.properties.name || '').trim().toLowerCase();
                                            const rev = countryRevenueMap[geoName] || 0;
                                            const intensity = rev > 0 ? 0.3 + 0.7 * (rev / maxRevenue) : 0;
                                            const fill = rev > 0
                                                ? `rgba(13, 110, 253, ${intensity})`
                                                : '#D6D6DA';
                                            return (
                                                <Geography
                                                    key={geo.rsmKey}
                                                    geography={geo}
                                                    fill={fill}
                                                    stroke="#FFFFFF"
                                                    strokeWidth={0.5}
                                                    style={{
                                                        default: { outline: 'none' },
                                                        hover: { fill: '#0d6efd', outline: 'none' },
                                                        pressed: { outline: 'none' },
                                                    }}
                                                />
                                            );
                                        })
                                    }
                                </Geographies>
                            </ComposableMap>

                            {/* Location table */}
                            {locations.length > 0 && (
                                <div className="table-responsive mt-2">
                                    <Table size="sm" hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>Location</th>
                                                <th className="text-end">Orders</th>
                                                <th className="text-end">Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {locations.slice(0, 8).map((loc, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        {[loc.city, loc.state, loc.country]
                                                            .filter(Boolean)
                                                            .join(', ') || '—'}
                                                    </td>
                                                    <td className="text-end">{loc.orders}</td>
                                                    <td className="text-end">{formatCurrency(loc.revenue)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                            {!loading && locations.length === 0 && (
                                <p className="text-muted text-center mt-3 mb-0" style={{ fontSize: 13 }}>
                                    No location data for this period.
                                </p>
                            )}
                        </CardBody>
                    </Card>
                </Col>

                {/* Top Selling Products */}
                <Col xl={6}>
                    <Card className="card-box mb-4">
                        <CardBody>
                            <h5 className="header-title mb-3">Top Selling Products</h5>
                            {loading ? (
                                <div className="text-center py-4">
                                    <Loader size="default" />
                                </div>
                            ) : topProducts.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Product</th>
                                                <th className="text-end">Qty</th>
                                                <th className="text-end">Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topProducts.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td className="text-muted">{idx + 1}</td>
                                                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {p.product_name}
                                                    </td>
                                                    <td className="text-end">{p.quantity}</td>
                                                    <td className="text-end">{formatCurrency(p.revenue)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <p className="text-muted text-center py-4 mb-0" style={{ fontSize: 13 }}>
                                    No orders in this period.
                                </p>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default Dashboard;
